// OrderForm.tsx
import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    Paper,
    Checkbox,
    FormControlLabel,
    Stack,
} from "@mui/material";
import { api, Balance, BasePrice, DiscountPrepayment, DiscountQuantity, LprOrder } from "./api";
import { useFetch } from "../../../hooks/useFetch2";
import InfoModal from './components/InfoModal';
import { useTranslation } from 'react-i18next';
import axios from "axios";
import { useCustomer } from "../../../contexts/CustomerContext";
import { usePresetDialogs } from "../../dialog";
import { formatDateTime } from "../../../tools/util";
import { useAuthStore } from "../../../store/useAuthStore3";

const downloadPdf = async () => {
    try {
      // 1. Делаем запрос. ВАЖНО: указываем responseType: 'blob'
      const response = await axios.get('https://api.example.com/get-pdf', {
        responseType: 'blob',
        headers: {
          'Authorization': 'Bearer YOUR_TOKEN'
        }
      });

      // 2. Создаем Blob из полученных данных
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // 3. Создаем URL для этого Blob
      const url = window.URL.createObjectURL(blob);

      // 4. Создаем временную ссылку для скачивания
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'document.pdf'); // Имя файла

      // 5. Добавляем в DOM, кликаем и удаляем
      document.body.appendChild(link);
      link.click();
      
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }

      // 6. Освобождаем память
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Ошибка при скачивании PDF:', error);
    }
};

interface AnaliticType {
    type: 'Embedding' | 'Face Detection' | 'Demography';
    count: number;
}

interface OrderParam {
    periodMon: number;
    config: AnaliticType[];
}

export const OrderType = {
  Subscription: 'monobank',
//   Manual: 'manual',
  Bank: 'bank',
  NumberOfCameras: 'numberOfCameras',
} as const;

export type OrderType = typeof OrderType[keyof typeof OrderType];

interface Order {
    app_id: string;
    order_id: string;
    amount: number;
    currency: string;
    description: string;
    param: OrderParam;
    liqpayData?: object;
    dt: Date;
    // type: 'liqpay'|'manual'|'bank'|'numberOfCameras';
    type: OrderType;
}

interface OrderFormProps {
    balance: Balance;
    onClose: () => void;
    onSubmit: (cameras: number, newEndDate: Date) => void;
}

const OrderForm: React.FC<OrderFormProps> = ({ balance, onClose, onSubmit, }) => {
    const { t } = useTranslation();
    const [periodMon, setPeriodMon] = useState(3);
    const [cameras, setCameras] = useState<number>(1);
    const [amount, setAmount] = useState(0);
    const [discount, setDiscount] = useState(0);
    const [currency, setCurrency] = useState<string>('');
    const [orderId, setOrderId] = useState('ORDER_ID_123');
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [isChecking, setIsChecking] = useState(false);
    const [method, setMethod] = useState<OrderType>(OrderType.Subscription);
    const { data: basePrice, setData: setBasePrice,  loading: basePriceLoading, error: basePriceError } = useFetch<BasePrice[]>(api.getBasePrice, []);
    const { data: cameraTotalCount, } = useFetch<number>(api.getCameraTotalCount, 0);
    const { data: cameraActiveCount, } = useFetch<number>(api.getCameraActiveCount, 0);
    const { data: discountPrepaiment, } = useFetch<DiscountPrepayment[]>(api.getDiscountPrepaiment, []);
    const { data: discountQuantity, } = useFetch<DiscountQuantity[]>(api.getDiscountQuantity, []);
    const { data: lprOrders, } = useFetch<LprOrder[]>(api.getLprOrder, []);
    const { customerId } = useAuthStore();
    const { data: isSubscribed, update: updateIsSubscribed } = useFetch<boolean>(api.getSubscription, false, customerId);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const { showConfirm, showInfo, DialogComponent } = usePresetDialogs();

    // console.log('discountPrepaiment', discountPrepaiment);
    // console.log('discountQuantity', discountQuantity);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAgreementAccepted, setIsAgreementAccepted] = useState(false);
    const [paymentDone, setPaymentDone] = useState(false);

    const [newEndDate, setNewEndDate] = useState<Date | undefined>(balance.endDate);
    const [kind, setKind] = useState<'offer' | 'privacy'>('offer');

    useEffect(() => {
        setCameras(lprOrders[0]?.order_count || 0);
    }, [lprOrders]);

    const translateCrn = (crn: string) => {
        switch(crn) {
            case 'UAH': return(t('UAH'));
            case 'EUR': return(t('EUR'));
        }
        return crn;
    }

    useEffect(() => {
        if (basePrice.length > 0) {
            setCurrency('UAH');
        }
    }, [basePrice.length]); // Следим только за длиной, а не за всем массивом    

    // const handleOpen = () => setIsModalOpen(true);
    const handleOpen = (kind: 'offer' | 'privacy') => {
        setIsModalOpen(true);
        setKind(kind);
    }

    const handleAccept = () => {
        setIsAgreementAccepted(true);
        setIsModalOpen(false);
    };
    const handleCancel = () => {
        setIsAgreementAccepted(false);
        setIsModalOpen(false);
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsAgreementAccepted(event.target.checked);
    };

    useEffect(() => {
        const basePriceRecord = basePrice.find(rec => rec.crn === currency);
        if (basePriceRecord?.value) {
            const disc1 = getDiscount1(discountPrepaiment, method == OrderType.Bank ? periodMon : 1); // periodMon
            const disc2 = getDiscount2(discountQuantity, cameras);
            const sum = (cameras ) * basePriceRecord.value;
            const newAmount = +(sum * (method == OrderType.Bank ? periodMon : 1) * disc1 * disc2).toFixed(2); // periodMon
            const newDiscount = +(sum * (method == OrderType.Bank ? periodMon : 1) - newAmount).toFixed(2); // periodMon
            setAmount(newAmount);
            setDiscount(newDiscount);

        } else {
            setAmount(0);
            setDiscount(0);
        }
    }, [cameras, periodMon, currency, basePrice, method]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMessage("");
        
        if (!isAgreementAccepted) {
            alert(t('OrderForm.PleaseAcceptAgreement'));
            return;
        }

        // if (cameras < (lprOrders[0]?.order_count || 0) ) {
        if (cameras < cameraActiveCount ) {
            // alert(t('OrderForm.PleaseTopup'));
            alert('Кількість активних камер перевищує Кількість замовлених');
            return;
        }

        if (!newEndDate || !customerId) {
            setErrorMessage('no new EndDate');
            return;
            // todo: show error
        }

        if (method == OrderType.NumberOfCameras) {
            const res = await api.changeOrder(customerId, cameras, newEndDate);
            if (res) {
                onSubmit(cameras, newEndDate);
            } else {
                setErrorMessage('Помилка збереження');
            }
            return;
        }

        if (method == OrderType.Subscription && !!isSubscribed) {
            const result = await showConfirm(
                'Кошти, що були зараховані раніше, будуть зараховані в подовженні строку підписки',
                'Підтвердіть дію',
            );
            if (!result) {
                return;
            } else {
                await api.suspendService(customerId);
            }
        }
            
        const param = {periodMon: (method == OrderType.Bank ? periodMon : 1), cameras, currency};
        const _orderId = 1;
        const order = await api.createOrder(amount, currency, t('OrderForm.Description', { orderId: _orderId }), param,method, );

        if (!!order && method == OrderType.Bank) {
            const blob = await api.printOrder(order);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `order_${order}.pdf`;
            link.click();
            if (link.parentNode) {
                link.parentNode?.removeChild(link);
            }
            window.URL.revokeObjectURL(url);
            // onClose();
            return;
        }

        if (!!order && method == OrderType.Subscription) {
            console.log('Order:', order);
            // const url = `https://cnt.theweb.place/api/billing/pay_liqpay/?order_id=${order}`;
            // const url = `https://cnt.theweb.place/api/billing/pay_monobank/?order_id=${order}`;
            const url = `/billing/pay_monobank/?order_id=${order}`;
            console.log('Opening URL:', url);
            const newWindow = window.open(url, '_blank');
            console.log('window.open called', newWindow);
            // setSuccessMessage(t('OrderForm.PaymentStartedMessage')); 
            // setIsChecking(true);
            // setOrderId(order);
            onSubmit(cameras, newEndDate);
            onClose();
        } else {
            alert(t('OrderForm.FailedToCreateOrder'));
        }
    };

    useEffect(() => {
        if (!isChecking) return;
        const intervalId = setInterval(async () => {
            try {
                const data = await api.getOrderStatus(orderId);
                if (data.result === 'ok') {
                    setSuccessMessage(t('OrderForm.PaymentSuccessMessage', { orderId }));
                    setIsChecking(false);
                    clearInterval(intervalId);
                    setPaymentDone(true);
                    onClose();
                }
                if (data.err_description) {
                    setSuccessMessage(data.err_description);
                }
            } catch (err) {
                console.error(t('OrderForm.ErrorFetchingStatus'), err);
            }
        }, 5000);
        return () => clearInterval(intervalId);
    }, [isChecking, orderId, t, onClose]);

    const onChangePeriodMon = (val: number) => {
        console.log('2 balance.endDate',balance.endDate);
        setPeriodMon(val);
        // Создаем копию, чтобы не мутировать пропс
        const startDate = balance.endDate ? new Date(balance.endDate) : new Date();
        startDate.setMonth(startDate.getMonth() + val);
        setNewEndDate(startDate);
    }

    useEffect(() => {
        const doWork = async () => {
            if (!customerId) return;

            if (method == OrderType.NumberOfCameras) {
                const newEndDate1 = await api.calcEndDate(customerId, cameras);
                setNewEndDate(newEndDate1);
                return;
            }
            const startDate = await api.calcEndDate(customerId, cameras);
            startDate.setMonth(startDate.getMonth() + ((method == OrderType.Bank ? periodMon : 1))); // periodMon
            setNewEndDate(startDate);
        };
        doWork();
    }, [balance.endDate, lprOrders, periodMon, method, cameras, customerId])

    return (
        <Paper elevation={3} sx={{ maxWidth: 420, mx: "auto", p: 3, mt: 4 }}>

            {DialogComponent}

            <form onSubmit={handleSubmit}>
                <Typography variant="h5" gutterBottom>
                    {t('OrderForm.Title')}
                </Typography>
                <Box mb={2} mt={4}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>{t('OrderForm.method')}</InputLabel>
                        <Select
                            value={method}
                            label={t('OrderForm.method')}
                            onChange={e => setMethod(e.target.value as OrderType)}
                        >
                            <MenuItem key={OrderType.Subscription} value={OrderType.Subscription}>{t('OrderForm.method_card')}</MenuItem>
                            <MenuItem key={OrderType.Bank} value={OrderType.Bank}>{t('OrderForm.method_bank')}</MenuItem>
                            <MenuItem key={OrderType.NumberOfCameras} value={OrderType.NumberOfCameras}>{t('OrderForm.method_numberOfCamera')}</MenuItem>
                            {/* numberOfCameras */}
                        </Select>
                    </FormControl>
                </Box>
                
                {method == OrderType.Subscription && (
                <Typography variant="body1" gutterBottom>
                    {t('OrderForm.liqpayDetail')}
                </Typography>
                )}

                {method == OrderType.Bank && (
                <Typography variant="body1" gutterBottom>
                    {t('OrderForm.bankDetail')}
                </Typography>
                )}

                {method == OrderType.NumberOfCameras && (
                <Typography variant="body1" gutterBottom>
                    {t('OrderForm.numberOfCamerasDetail')}
                </Typography>
                )}

                {method == OrderType.Bank && (
                <Box mb={2} mt={4} >
                    <TextField
                        label={t('OrderForm.PeriodLabel')}
                        type="number"
                        size="small"
                        inputProps={{ min: 1 }}
                        value={periodMon}
                        // onChange={e => setPeriodMon(Number(e.target.value || '0'))}
                        onChange={e => onChangePeriodMon(Number(e.target.value || '0'))}
                        sx={{ width: 150 }}
                    />
                </Box>
                )}

                {(method == OrderType.Subscription || method == OrderType.Bank) && (
                <Box mb={2}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>{t('OrderForm.CurrencyLabel')}</InputLabel>
                        <Select
                            value={currency}
                            label={t('OrderForm.CurrencyLabel')}
                            onChange={e => setCurrency(e.target.value)}
                        >
                            {basePrice.map(rec => (
                                <MenuItem key={rec.crn} value={rec.crn}>{translateCrn(rec.crn)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                )}

                <Box mb={2}>
                    <TextField
                        label={t('OrderForm.CamerasTitle')}
                        type="number"
                        size="small"
                        inputProps={{ min: 0 }}
                        value={cameras}
                        onChange={e => setCameras(Number(e.target.value))}
                        sx={{ width: 150 }}
                    />
                </Box>

                <Box my={2}>
                    {(method == OrderType.Subscription || method == OrderType.Bank) && (
                    <Typography variant="h6">
                        {t('OrderForm.TotalAmount', { amount, currency })}
                    </Typography>
                    )}
                    {(method == OrderType.Subscription || method == OrderType.Bank) && (
                    <Typography variant="h6">
                        {t('OrderForm.TotalDiscount', {amount: discount, currency })}
                    </Typography>
                    )}
                    
                    <Typography variant="h6">
                        {t('OrderForm.newEndDate')} {formatDateTime(newEndDate)}
                    </Typography>
                </Box>
                
                <Box sx={{ mb: 2 }}>
                    {/* <FormControlLabel
                        control={
                            <Checkbox
                                checked={isAgreementAccepted}
                                onChange={handleCheckboxChange}
                            />
                        }
                        label={
                            <Typography
                                onClick={handleOpen}
                                sx={{
                                    cursor: 'pointer',
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                    '&:hover': {
                                        textDecoration: 'none',
                                    },
                                }}
                            >
                                {t('OrderForm.AgreementLink')}
                            </Typography>
                        }
                    /> */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isAgreementAccepted}
                                onChange={handleCheckboxChange}
                            />
                        }
                        label={
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                З умовами{' '}
                                <Typography
                                    component="span"
                                    onClick={() => handleOpen('offer')}
                                    sx={{
                                        cursor: 'pointer',
                                        color: 'primary.main',
                                        textDecoration: 'underline',
                                        '&:hover': { textDecoration: 'none' },
                                    }}
                                >
                                    Публічної оферти
                                </Typography>
                                {' '}та{' '}
                                <Typography
                                    component="span"
                                    onClick={() => handleOpen('privacy')}
                                    sx={{
                                        cursor: 'pointer',
                                        color: 'primary.main',
                                        textDecoration: 'underline',
                                        '&:hover': { textDecoration: 'none' },
                                    }}
                                >
                                    Політики конфіденційності
                                </Typography>
                                {' '}згоден
                            </Typography>
                        }
                    />
                </Box>

                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{ fontWeight: "bold", px: 4 }}
                        disabled={!isAgreementAccepted || paymentDone}
                    >
                        {buttonCaption(method, t('OrderForm.PayButton'), t('OrderForm.BankButton'), t('OrderForm.CamerasButton'))}
                    </Button>
                    <Button
                        type="button"
                        onClick={onClose}
                        variant="outlined"
                        color="secondary"
                    >
                        {t('OrderForm.ExitButton')}
                    </Button>
                </Stack>

                {/* {isChecking && !successMessage && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        {t('OrderForm.CheckingPayment')}
                    </Typography>
                )}
                {successMessage && (
                    <Typography variant="body1" color="success.main" sx={{ mt: 2 }}>
                        {successMessage}
                    </Typography>
                )} */}
            </form>

            {errorMessage && (
                <Typography mt={2} color='error'>{errorMessage}</Typography>
            )}      
            
            <InfoModal
                open={isModalOpen}
                onCancel={handleCancel}
                onAccept={handleAccept}
                kind={kind}
            />
        </Paper>
    );
};

export default OrderForm;

function getDiscount1(discountsPrepayment: DiscountPrepayment[], periodMon: number): number {
    const arr = discountsPrepayment.sort((i1,i2) => i1.from - i2.from);
    const range = arr.find(item => periodMon >= item.from && periodMon <= item.to);
    return (100 - (range?.value_pc || 0)) / 100;
}

function getDiscount2(discountsQuantity: DiscountQuantity[], cameras: number): number {
    const arr = discountsQuantity.sort((i1,i2) => i1.from - i2.from);
    const range = arr.find(item => cameras >= item.from && cameras <= item.to);
    return (100 - (range?.value_pc || 0)) / 100;
}

function buttonCaption(method: OrderType, optliqpay: string, optbank: string, optcameras: string): string {
    switch (method) {
        case OrderType.Subscription: return optliqpay;
        case OrderType.Bank: return optbank;
        case OrderType.NumberOfCameras: return optcameras;
    }
    return '';
}

/*
id from to pc
1	2	4	10
2	5	9	20
3	10	19	30
4	20	99	35
5	100	9999 40
*/
